import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { first } from 'rxjs';

import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsIconButtonComponent } from '@design-system/ds-icon-button/component/ds-icon-button.component';
import { DsInputComponent } from '@design-system/ds-input/component/ds-input.component';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { NotificationService } from '@notification/service';
import { IOrganTypeMappingViewModel } from 'src/app/service/service-request';
import { ServiceRequestService } from 'src/app/service/service-request.service';
import { PoMappingInterface, PoMappingModalDataInterface } from '../interfaces/po-mapping-modal-data.interface';

type PoMappingRowGroup = FormGroup<{
  organType: FormControl<string>;
  organTypeValue: FormControl<string>;
}>;

/**
 * PO# mapping modal — lets the user enter a PO number per organ type for a customer.
 */
@Component({
  selector: 'app-po-mapping-modal',
  standalone: true,
  imports: [NgIf, NgFor, ReactiveFormsModule, DsTextComponent, DsInputComponent, DsButtonComponent, DsIconButtonComponent],
  templateUrl: './po-mapping-modal.component.html',
  styleUrls: ['./po-mapping-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoMappingModalComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly serviceRequestService = inject(ServiceRequestService);

  private readonly notificationService = inject(NotificationService);

  private readonly dialogRef = inject<MatDialogRef<PoMappingModalComponent, PoMappingInterface | null>>(MatDialogRef);

  private readonly dialogData = inject<PoMappingModalDataInterface>(MAT_DIALOG_DATA);

  isLoading = true;

  readonly organTypes: FormArray<PoMappingRowGroup> = this.formBuilder.array<PoMappingRowGroup>([]);

  readonly poForm: FormGroup<{ organType: FormArray<PoMappingRowGroup> }> = this.formBuilder.group({
    organType: this.organTypes,
  });

  ngOnInit(): void {
    this.loadOrganTypes();
  }

  onClose(): void {
    this.dialogRef.close(null);
  }

  onSave(): void {
    const poBox: PoMappingInterface = { ...(this.dialogData.poBox ?? {}) };

    this.organTypes.controls.forEach((row: PoMappingRowGroup): void => {
      const { organType, organTypeValue } = row.getRawValue();

      poBox[organType] = organTypeValue;
    });

    this.dialogRef.close(poBox);
  }

  /**
   * Loads the organ types and builds a PO# row per organ, pre-filled with the value saved on the customer.
   */
  private loadOrganTypes(): void {
    this.serviceRequestService
      .getOrganTypes()
      .pipe(first())
      .subscribe({
        next: (response: IOrganTypeMappingViewModel[]): void => {
          response?.forEach((item: IOrganTypeMappingViewModel): void => {
            this.organTypes.push(
              this.formBuilder.group({
                organType: this.formBuilder.control<string>(item.organTypeValue, { nonNullable: true }),
                organTypeValue: this.formBuilder.control<string>(this.dialogData.poBox?.[item.organTypeValue] ?? '', {
                  nonNullable: true,
                }),
              }),
            );
          });

          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
        error: (): void => {
          this.notificationService.warning('Something went wrong! Please try again', 'Warning');
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }
}
